import { z } from 'zod';

import { CodeLanguage, type UiLanguage } from './languages.js';

export const ExerciseCategory = z.enum([
  'sequences',
  'conditions',
  'loops',
  'digits',
  'arrays',
  'strings',
]);
export type ExerciseCategory = z.infer<typeof ExerciseCategory>;

/** Chips shown under "Blocks you might need"; the web app maps each to a label and a color. */
export const HintBlock = z.enum([
  'while',
  'repeat',
  'for',
  'if',
  'ifElse',
  'mod',
  'intDiv',
  'read',
  'print',
  'compare',
  'and',
  'change',
  'join',
  'break',
  'list',
  'table',
  'index',
  'length',
  'letter',
  'code',
  'case',
  'find',
  'part',
]);
export type HintBlock = z.infer<typeof HintBlock>;

const Localized = z.object({ ro: z.string().min(1), en: z.string().min(1) });

export const ExerciseExample = z.object({
  /** stdin, one value per line. */
  input: z.string(),
  /** Expected stdout, compared after trimming trailing whitespace on each line. */
  output: z.string(),
  /** Teacher-authored exercises only: checked on the server, never sent to the browser. */
  hidden: z.boolean().optional(),
});
export type ExerciseExample = z.infer<typeof ExerciseExample>;

/** Why one example run ended. The student sees this next to the example. */
export const ExampleStatus = z.enum(['ok', 'wrong-output', 'error', 'needs-input', 'too-long']);
export type ExampleStatus = z.infer<typeof ExampleStatus>;

/**
 * One example run: what the exercise asks for, what the program printed, and whether they match.
 * Produced by `checkExamples` in @pebblecode/codegen, both in the browser and on the server.
 */
export const ExampleResult = z.object({
  /** A hidden example's input, expected and actual output are blanked; only the status stays. */
  hidden: z.boolean().optional(),
  input: z.string(),
  expected: z.string(),
  actual: z.string(),
  status: ExampleStatus,
  /** The runtime error, when the run failed; `code` is a codegen `RuntimeErrorCode`. */
  error: z
    .object({
      code: z.string(),
      blockId: z.string().nullable(),
      params: z.record(z.string(), z.string()),
    })
    .nullable(),
});
export type ExampleResult = z.infer<typeof ExampleResult>;

/**
 * One file in content/exercises. Statements use a tiny markup: `code` and **bold**.
 * Reference solutions are not here — they live in test fixtures and are never served.
 */
export const ExerciseContent = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/),
  category: ExerciseCategory,
  position: z.number().int().positive(),
  difficulty: z.number().int().min(1).max(3),
  defaultLang: CodeLanguage.nullable().default(null),
  title: Localized,
  summary: Localized,
  statement: Localized,
  examples: z.array(ExerciseExample).min(1),
  hintBlocks: z.array(HintBlock).default([]),
  /** Blockly workspace JSON to start from; the empty program when absent. */
  starter: z.record(z.string(), z.unknown()).nullable().default(null),
});
export type ExerciseContent = z.infer<typeof ExerciseContent>;

/** An exercise as the API serves it, localized. */
export const ExerciseDto = z.object({
  slug: z.string(),
  category: ExerciseCategory,
  position: z.number(),
  /** Number of exercises in the same category ("Exercise 4 of 12"). */
  total: z.number(),
  difficulty: z.number(),
  defaultLang: CodeLanguage.nullable(),
  title: z.string(),
  summary: z.string(),
  statement: z.string(),
  examples: z.array(ExerciseExample),
  hintBlocks: z.array(HintBlock),
  starter: z.record(z.string(), z.unknown()).nullable(),
  /** Examples checked on the server that the student does not see (teacher exercises). */
  hiddenCount: z.number().default(0),
  /** Written by a teacher rather than part of the catalog. */
  custom: z.boolean().default(false),
});
export type ExerciseDto = z.infer<typeof ExerciseDto>;

export const ExerciseSummary = ExerciseDto.pick({
  slug: true,
  category: true,
  position: true,
  total: true,
  difficulty: true,
  title: true,
  summary: true,
});
export type ExerciseSummary = z.infer<typeof ExerciseSummary>;

export const ExerciseListResponse = z.object({
  categories: z.array(ExerciseCategory),
  exercises: z.array(ExerciseSummary),
});
export type ExerciseListResponse = z.infer<typeof ExerciseListResponse>;

export function localizeExercise(e: ExerciseContent, lang: UiLanguage, total: number): ExerciseDto {
  const visible = e.examples.filter((x) => !x.hidden);
  return {
    slug: e.slug,
    category: e.category,
    position: e.position,
    total,
    difficulty: e.difficulty,
    defaultLang: e.defaultLang,
    title: e.title[lang],
    summary: e.summary[lang],
    statement: e.statement[lang],
    examples: visible.map(({ input, output }) => ({ input, output })),
    hintBlocks: e.hintBlocks,
    starter: e.starter,
    hiddenCount: e.examples.length - visible.length,
    custom: false,
  };
}

/** Normalize program output for comparison: unify newlines, trim line ends and the end. */
export function normalizeOutput(s: string): string {
  return s
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((l) => l.trimEnd())
    .join('\n')
    .trimEnd();
}
